/**
 * The constructor type for WebVTTCue implementations. It matches the WebVTT
 * specification for VTTCue.
 */

/**
 * The ultimate fallback WebVTTCue implementation, when the client doesn't
 * offer a VTTCue or TextTrackCue class. It has the getCueAsHTML() method, but
 * it only returns plain text.
 */
class PlainTextVTTCue {
  constructor(startTime, endTime, text) {
    this.startTime = void 0;
    this.endTime = void 0;
    this.text = void 0;
    this.id = void 0;
    this.vertical = '';
    this.snapToLines = true;
    this.line = 'auto';
    this.lineAlign = 'start';
    this.position = 'auto';
    this.positionAlign = 'auto';
    this.size = 100;
    this.align = 'center';
    this.startTime = startTime;
    this.endTime = endTime;
    this.text = text;
  }
  /**
   * Simplified implementation. Not according to spec. Just strips the tags and
   * expects the rest to be plain text (HTML entities not supported).
   */


  getCueAsHTML() {
    const doc = document.createDocumentFragment();
    doc.textContent = this.text.replace(/<[/\w][^<]*>/g, '');
    return doc;
  }

}
/**
 * The best WebVTTCue implementation available.
 */

const WebVTTCueImpl = typeof VTTCue !== 'undefined' ? VTTCue // Modern browsers.
: typeof TextTrackCue !== 'undefined' ? TextTrackCue // Internet Explorer.
: PlainTextVTTCue; // Ultimate fallback/NodeJS.

/**
 * Convenience function for creating a WebVTTCue object from the best
 * implementation available.
 */

function createWebVTTCue(data) {
  const cue = new WebVTTCueImpl(data.startTime, data.endTime, data.text);
  cue.id = data.id;
  cue.vertical = data.vertical;
  cue.snapToLines = data.snapToLines;
  cue.line = data.line;
  cue.lineAlign = data.lineAlign;
  cue.position = data.position;
  cue.positionAlign = data.positionAlign;
  cue.size = data.size;
  cue.align = data.align;
  return cue;
}

/**
 * Supported:
 * - Cue blocks - Payload not processed.
 *
 * Not supported:
 * - Style blocks - Ignored if present.
 * - Regions - Ignored if present.
 *
 * While this module parses WebVTT data, it doesn't follow Section 6: "Parsing"
 * in the WebVTT specification accurately. This is not an "incremental WebVTT
 * parser". The WebVTT data is expected to already be loaded. Not all values
 * are validated as carefully as section 6 specifies.
 */
/**
 * A parsed WebVTT file. Regions and style blocks are not supported.
 */

/**
 * Parse the contents of a WebVTT file and return a list of cues.
 */
function parseWebVTT(data) {
  // Normalize null characters and line breaks according to spec.
  const lines = data.replace(/\0/g, '�').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  if (lines.length === 0 || !/^WEBVTT\b/.exec(lines[0])) {
    throw new Error('Not WebVTT data.');
  }

  const cues = [];
  let i = 0;

  while (++i < lines.length) {
    if (!/[\d:.]+\s+-->\s+[\d:.]+/.exec(lines[i])) {
      continue;
    }

    const id = lines[i - 1] !== '' ? lines[i - 1] : undefined;
    const cueConfig = lines[i].split(/\s+/);
    const startTime = parseWebVTTCueTimestamp(cueConfig[0]);
    const endTime = parseWebVTTCueTimestamp(cueConfig[2]);
    const settings = parseWebVTTCueSettings(cueConfig.slice(3));
    const payload = [];

    while (++i < lines.length && lines[i] !== '') {
      payload.push(lines[i]);
    }

    cues.push(createWebVTTCue({
      id,
      startTime,
      endTime,
      text: payload.join('\n'),
      ...settings
    }));
  }

  return {
    cues
  };
}
/**
 * Convert a WebVTT Cue Timestamp (mm:ss.sss or hhh*:mm:ss.sss) to total
 * seconds. No validation.
 */

function parseWebVTTCueTimestamp(timestamp) {
  const values = timestamp.split(':');
  return values.map(value => Number.parseFloat(value)).reduce((totalSeconds, value, index) => totalSeconds + value * 60 ** (values.length - 1 - index), 0);
}
/**
 * Parse a WebVTT Cue Settings List, either from a string or already split by
 * whitespace.
 *
 * Ref sections 3.3 and 4.4 in the WebVTT specification. Section 6 about
 * parsing is not followed accurately:
 * - parseInt and parseFloat allow trailing characters, which aren't allowed in
 *   the WebVTT data, but it isn't validated here.
 * - The line and lineAlign values should both be dropped if one of them is
 *   invalid. Here, valid values are kept. Same with position and
 *   positionAlign.
 * - Percentages aren't validated to be between 0 and 100, inclusively. But the
 *   computed values algorithms in the WebVTT specification, if implemented,
 *   validate the ranges.
 *
 * Region setting is not supported.
 */


function parseWebVTTCueSettings(rawSettings) {
  const rawSettingsArray = Array.isArray(rawSettings) ? rawSettings : rawSettings.split(/\s+/);
  const settings = {
    vertical: '',
    snapToLines: true,
    line: 'auto',
    lineAlign: 'start',
    position: 'auto',
    positionAlign: 'auto',
    align: 'center',
    size: 100
  };

  for (const rawSetting of rawSettingsArray) {
    // A setting has a name, an optional colon and an optional value.
    const colonIndex = rawSetting.indexOf(':');
    const name = colonIndex > 0 ? rawSetting.substr(0, colonIndex) : rawSetting;
    const value = colonIndex > 0 ? rawSetting.substr(colonIndex + 1) : '';

    if (name === 'vertical') {
      if (value === 'lr' || value === 'rl') {
        settings.vertical = value;
      }
    } else if (name === 'line') {
      const [offset, lineAlign] = value.split(',');
      let parsedOffset;

      if (offset.charAt(offset.length - 1) === '%') {
        settings.snapToLines = false; // Percentages can be floats by spec. It should be in the range 0..100.

        parsedOffset = Number.parseFloat(offset);
      } else {
        parsedOffset = Number.parseInt(offset, 10);
      }

      if (!Number.isNaN(parsedOffset)) {
        settings.line = parsedOffset;
      }

      if (lineAlign !== undefined && ['start', 'center', 'end'].includes(lineAlign)) {
        settings.lineAlign = lineAlign;
      }
    } else if (name === 'position') {
      const [position, positionAlign] = value.split(',');
      const parsedPosition = Number.parseFloat(position);

      if (!Number.isNaN(parsedPosition)) {
        settings.position = parsedPosition;
      }

      if (positionAlign !== undefined && ['line-left', 'center', 'line-right'].includes(positionAlign)) {
        settings.positionAlign = positionAlign;
      }
    } else if (name === 'size') {
      const parsedSize = Number.parseFloat(value);

      if (!Number.isNaN(parsedSize)) {
        settings.size = parsedSize;
      }
    } else if (name === 'align') {
      if (['start', 'center', 'end', 'left', 'right'].includes(value)) {
        settings.align = value;
      }
    }
  }

  return settings;
}

let _Symbol$toStringTag;

_Symbol$toStringTag = Symbol.toStringTag;

/**
 * A promise that can be resolved and rejected from outside itself.
 */
class ExposedPromise {
  constructor() {
    this.__resolved = false;
    this.__resolvedValue = void 0;
    this.__resolve = void 0;
    this.__rejected = false;
    this.__rejectedReason = void 0;
    this.__reject = void 0;
    this.then = void 0;
    this.catch = void 0;
    this.finally = void 0;
    this[_Symbol$toStringTag] = 'ExposedPromise';
    const promise = new Promise((resolve, reject) => {
      this.__resolve = resolve;
      this.__reject = reject;

      if (this.__resolved) {
        resolve(this.__resolvedValue);
      }

      if (this.__rejected) {
        reject(this.__rejectedReason);
      }
    });
    this.then = promise.then.bind(promise);
    this.catch = promise.catch.bind(promise); // Finally not always supported?

    if ('finally' in promise) {
      this.finally = promise.finally.bind(promise);
    } else {
      this.finally = () => {
        throw new Error('Finally not supported.');
      };
    }
  }

  resolve(value) {
    var _this$__resolve;

    this.__resolved = !this.__rejected; // First wins.

    this.__resolvedValue = value;
    (_this$__resolve = this.__resolve) == null ? void 0 : _this$__resolve.call(this, value);
  }

  reject(reason) {
    var _this$__reject;

    this.__rejected = !this.__resolved; // First wins.

    this.__rejectedReason = reason;
    (_this$__reject = this.__reject) == null ? void 0 : _this$__reject.call(this, reason);
  }

}

export { ExposedPromise as E, parseWebVTT as p };
